// A phone field with its country in one box: the country (flag + dial code, the UK first and by default) on the left,
// the national number beside it. Pasting "+91 98765…" or "0044 7700…" picks the country from the number. The value in and out is the
// whole number in E.164 (+447700900123), so forms keep their data shape; a stored value with a dial code pre-selects
// its country. The dial codes and countries come from libphonenumber-js (lib/phone.ts). `kit` picks the styling of the
// context it sits in: the admin kit, the website kit or the tenant portal.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { countryOfValue, countryOptions, DEFAULT_COUNTRY, flagOf, phoneValueOf, splitPhone, type CountryCode } from '../../lib/phone';

export type PhoneKit = 'admin' | 'site' | 'tenant';

export interface PhoneInputProps extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'defaultValue'> {
  /** The stored number: '' / null when empty, otherwise E.164 (older values in any format still show). */
  value: string | null | undefined;
  /** '' when the number is cleared, otherwise E.164 for the chosen country and the digits typed. */
  onChange: (value: string) => void;
  kit?: PhoneKit;
  invalid?: boolean;
  /** Classes for the box (width / layout); `className` goes on the number input itself. */
  wrapperClassName?: string;
}

/**
 * One box in the kit's own look: the country (flag + dial code) on the left behind a hairline, the national number on
 * the right. A transparent native select sits over the country part and does the picking (keyboard search, phone
 * pickers). The box carries the border and the focus ring; the input inside has neither.
 */
const BOX: Record<PhoneKit, string> = {
  admin: 'h-8 rounded-lg border border-ink-200 bg-white text-[12.5px] focus-within:border-bronze-500 focus-within:ring-2 focus-within:ring-bronze-500/30',
  site: 'min-h-[48px] rounded-[12px] border border-ink-200 bg-white text-[16px] hover:border-ink-300 focus-within:border-bronze-400 focus-within:[box-shadow:var(--site-ring)] sm:text-[15px]',
  tenant: 'min-h-[48px] rounded-[14px] border border-ink-300 bg-white text-[16px] focus-within:border-bronze-500',
};
const COUNTRY: Record<PhoneKit, string> = { admin: 'gap-1 px-2', site: 'gap-1.5 pl-3.5 pr-2.5', tenant: 'gap-1.5 pl-3.5 pr-2.5' };
const NUMBER: Record<PhoneKit, string> = { admin: 'px-2', site: 'px-3.5', tenant: 'px-3.5' };
const INVALID = '!border-[#A85A4C]';

export function PhoneInput({ value, onChange, kit = 'admin', invalid, className = '', wrapperClassName = 'w-full', placeholder, disabled, ...rest }: PhoneInputProps) {
  const first = useMemo(() => splitPhone(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [country, setCountry] = useState<CountryCode>(first.country);
  const [text, setText] = useState(first.national);
  const sent = useRef<string | null>(null);
  // A value set from outside (a record loading, a form reset) replaces the field; our own echo does not.
  useEffect(() => {
    if ((value ?? '') === sent.current) return;
    sent.current = null;
    const s = splitPhone(value, country);
    setCountry(s.country); setText(s.national);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (national: string, c: CountryCode) => { const out = phoneValueOf(national, c); sent.current = out; onChange(out); };
  const type = (raw: string) => {
    const pasted = raw.trim().replace(/^00(?=[1-9])/, '+');
    if (pasted.startsWith('+')) {
      const c = countryOfValue(pasted);
      if (c) { const s = splitPhone(pasted, c); setCountry(s.country); setText(s.national); emit(s.national, s.country); return; }
    }
    const national = raw.replace(/[^\d ()-]/g, '');
    setText(national); emit(national, country);
  };
  const pick = (c: CountryCode) => { setCountry(c); emit(text, c); };

  const opts = countryOptions();
  const dial = opts.find((o) => o.code === country)?.dial ?? '';
  const props = {
    ...rest,
    type: 'tel' as const,
    inputMode: 'tel' as const,
    autoComplete: rest.autoComplete ?? 'tel-national',
    value: text,
    placeholder: placeholder ?? (country === DEFAULT_COUNTRY ? '7700 900123' : undefined),
    disabled,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => type(e.target.value),
    'aria-invalid': invalid || rest['aria-invalid'] || undefined,
  };
  return (
    <div className={`flex min-w-0 items-stretch overflow-hidden text-ink-900 transition ${BOX[kit]} ${disabled ? 'bg-ink-50 text-ink-400' : ''} ${invalid ? INVALID : ''} ${wrapperClassName}`}>
      <div className={`relative flex shrink-0 items-center border-r border-ink-200 ${COUNTRY[kit]} ${disabled ? 'text-ink-400' : 'text-ink-800'}`}>
        <span aria-hidden className="leading-none">{flagOf(country)}</span>
        <span aria-hidden className="tabular-nums">{dial}</span>
        <ChevronDown aria-hidden className="h-3.5 w-3.5 text-ink-400" />
        <select aria-label="Country code" value={country} disabled={disabled} onChange={(e) => pick(e.target.value as CountryCode)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed">
          {opts.map((o) => <option key={o.code} value={o.code}>{o.flag} {o.name} ({o.dial})</option>)}
        </select>
      </div>
      <input {...props} className={`min-w-0 flex-1 bg-transparent text-inherit outline-none placeholder:text-ink-400 disabled:cursor-not-allowed ${NUMBER[kit]} ${className}`} />
    </div>
  );
}
